// Domain Models - Using recovery data structure since sleep data is in recovery table
export interface SleepReading {
  recoveryId: number;
  organisationId: number;
  firstName: string;
  lastName: string;
  cycleId: number;
  date: Date;
  dayOfWeek: string;
  sleepScore: number;
  sleepOnset: Date;
  wakeOnset: Date;
  hoursInBed: number;
  hoursOfSleep: number;
  sleepNeed: number;
  sleepEfficiency: number;
  wakePeriods: number;
  sleepDisturbances: number;
  latency: number;
  cycles: number;
  remSleepHours: number;
  deeplSleepHours: number;
  lightSleepHours: number;
  awakeHours: number;
  missingDataHours: number;
  sleepDebtHours: number;
  respiratoryRate: number;
  lastNapEndTime: Date;
  napTimeInBed: number;
  totalCycleNapTime: number;
  napDisturbances: number;
  napRemHours: number;
  napSwsHours: number;
  napLightHours: number;
  napWakeHours: number;
  napMissingDataHours: number;
  totalCycleTimeHours: number;
  timezoneOffset: string;
  oxygenSaturation: number;
  skinTemp: number;
  optimalSleepOnset: Date;
  optimalWakeOnset: Date;
  remPercentage: number;
  deepSleepPercentage: number;
  restorativeSleepHours: number;
  restorativeSleepPercentage: number;
  sleepConsistencyPercentage: number;
  shipId: number;
}

export interface SleepStage {
  name: string;
  duration: number;
  percentage: number;
  color: string;
  description: string;
}

export interface SleepMetrics {
  averageDuration: number;
  averageScore: number;
  totalSleep: number;
  sleepDebt: number;
  trend: 'improving' | 'declining' | 'stable';
}

// Domain Services
export class SleepAnalyzer {
  static calculateSleepScore(efficiency: number, duration: number, deepSleep: number): number {
    const efficiencyScore = Math.min(efficiency || 0, 100) * 0.4;
    const durationScore = Math.min((duration / 8) * 100, 100) * 0.4; // 8 hours target
    const deepSleepScore = Math.min(((deepSleep || 0) / 1.5) * 100, 100) * 0.2; // 1.5 hours deep sleep target
    return efficiencyScore + durationScore + deepSleepScore;
  }

  static getPerformanceZone(score: number): { label: string; color: string; description: string } {
    if (score >= 85) return { label: 'Excellent', color: '#00C851', description: 'Outstanding sleep quality' };
    if (score >= 70) return { label: 'Good', color: '#4ECDC4', description: 'Good sleep quality' };
    if (score >= 60) return { label: 'Fair', color: '#FFB347', description: 'Adequate sleep with room for improvement' };
    return { label: 'Poor', color: '#FF4444', description: 'Sleep quality needs attention' };
  }

  static calculateTrend(data: number[], timeRange: '7d' | '30d' | '90d'): 'improving' | 'declining' | 'stable' {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    if (data.length < 4) return 'stable';
    const recent = data.slice(-7).reduce((a, b) => a + b, 0) / 7;
    const previous = data.slice(-days, -7).reduce((a, b) => a + b, 0) / 7;
    const diff = previous - recent;
    return Math.abs(diff) < 2 ? 'stable' : diff > 0 ? 'improving' : 'declining';
  }

  static formatTime(hours: number): string {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  }

  static formatTimeString(timeDate: Date): string {
    if (!timeDate) return 'N/A';
    const date = new Date(timeDate);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  // Aggregate data by date - fixed to use proper sleep data fields
  static aggregateDataByDate(data: SleepReading[]): SleepReading[] {
    if (!data || data.length === 0) return [];

    // Group data by date
    const groupedByDate = data.reduce((groups, item) => {
      const dateKey = new Date(item.date).toISOString().split('T')[0];
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
      return groups;
    }, {} as Record<string, SleepReading[]>);

    // Aggregate each date group into a single record
    return Object.entries(groupedByDate).map(([dateKey, dayData]) => {
      const validData = dayData.filter(item =>
        item.sleepScore !== null &&
        item.sleepScore !== undefined &&
        !isNaN(item.sleepScore)
      );

      if (validData.length === 0) {
        // Return a default record if no valid data
        return {
          ...dayData[0],
          date: new Date(dateKey),
          sleepScore: 0,
          hoursOfSleep: 0,
          sleepDebtHours: 0,
          remSleepHours: 0,
          deeplSleepHours: 0,
          lightSleepHours: 0,
          awakeHours: 0
        };
      }

      // Helper function to safely average numeric values
      const safeAverage = (field: keyof SleepReading) => {
        const values = validData.map(item => item[field]).filter(val => val !== null && val !== undefined && !isNaN(Number(val)));
        return values.length > 0 ? values.reduce((sum: number, val) => sum + Number(val), 0) / values.length : 0;
      };

      return {
        recoveryId: dayData[0].recoveryId,
        organisationId: dayData[0].organisationId,
        firstName: `${validData.length} crew members`,
        lastName: '',
        cycleId: dayData[0].cycleId,
        date: new Date(dateKey),
        dayOfWeek: dayData[0].dayOfWeek,
        sleepScore: safeAverage('sleepScore'),
        sleepOnset: dayData[0].sleepOnset,
        wakeOnset: dayData[0].wakeOnset,
        hoursInBed: safeAverage('hoursInBed'),
        hoursOfSleep: safeAverage('hoursOfSleep'),
        sleepNeed: safeAverage('sleepNeed'),
        sleepEfficiency: safeAverage('sleepEfficiency'),
        wakePeriods: safeAverage('wakePeriods'),
        sleepDisturbances: safeAverage('sleepDisturbances'),
        latency: safeAverage('latency'),
        cycles: safeAverage('cycles'),
        remSleepHours: safeAverage('remSleepHours'),
        deeplSleepHours: safeAverage('deeplSleepHours'),
        lightSleepHours: safeAverage('lightSleepHours'),
        awakeHours: safeAverage('awakeHours'),
        missingDataHours: safeAverage('missingDataHours'),
        sleepDebtHours: safeAverage('sleepDebtHours'),
        respiratoryRate: safeAverage('respiratoryRate'),
        lastNapEndTime: dayData[0].lastNapEndTime,
        napTimeInBed: safeAverage('napTimeInBed'),
        totalCycleNapTime: safeAverage('totalCycleNapTime'),
        napDisturbances: safeAverage('napDisturbances'),
        napRemHours: safeAverage('napRemHours'),
        napSwsHours: safeAverage('napSwsHours'),
        napLightHours: safeAverage('napLightHours'),
        napWakeHours: safeAverage('napWakeHours'),
        napMissingDataHours: safeAverage('napMissingDataHours'),
        totalCycleTimeHours: safeAverage('totalCycleTimeHours'),
        timezoneOffset: dayData[0].timezoneOffset,
        oxygenSaturation: safeAverage('oxygenSaturation'),
        skinTemp: safeAverage('skinTemp'),
        optimalSleepOnset: dayData[0].optimalSleepOnset,
        optimalWakeOnset: dayData[0].optimalWakeOnset,
        remPercentage: safeAverage('remPercentage'),
        deepSleepPercentage: safeAverage('deepSleepPercentage'),
        restorativeSleepHours: safeAverage('restorativeSleepHours'),
        restorativeSleepPercentage: safeAverage('restorativeSleepPercentage'),
        sleepConsistencyPercentage: safeAverage('sleepConsistencyPercentage'),
        shipId: dayData[0].shipId
      };
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()); // Sort by date
  }

  static filterDataByTimeRange(data: SleepReading[], timeRange: '7d' | '30d' | '90d'): SleepReading[] {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const cutoffDate = new Date(
      Math.max(...data.map(obj => new Date(obj.date).getDate()))
    );
    cutoffDate.setDate(cutoffDate.getDate() - days);

    // Filter by date and ensure we get exactly the requested number of days
    const filtered = data.filter(item => new Date(item.date) >= cutoffDate);

    // Sort by date and take the last N days to ensure we have the exact count
    const sorted = filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return sorted.slice(-days);
  }
}
